/**
 * 템플릿 글꼴 — 콘솔에서 템플릿마다 고른다.
 *
 * 글꼴 파일(`packages/core/fonts/*.woff2`)은 **앱 번들에 싣는다.** 인터넷이 없는 현장(USB 설치)도
 * 있어 CDN 에서 받지 않는다. 받는 쪽(플레이어·콘솔)이 자기 번들의 주소를 알려 주면
 * `templateFontFaceCss` 가 `@font-face` 를 만든다 — 이 패키지는 주소를 모른다(빌드 도구가 정한다).
 *
 * 고른 기준과 라이선스(셋 다 SIL OFL 1.1)는 `scripts/template-fonts.mjs` 머리말에 있다.
 * 굵기는 템플릿이 쓰는 500·700·800 셋만 싣는다 — 600 은 700 으로 맞춰진다(CSS 글꼴 고르기 규칙).
 */
export const TEMPLATE_FONT_IDS = ['pretendard', 'nanum-square-neo', 'noto-serif-kr'];
/** 고르지 않은 템플릿(글꼴을 넣기 전에 만든 것 포함)의 글꼴. */
export const DEFAULT_TEMPLATE_FONT = 'pretendard';
const SANS_FALLBACK = "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', 'Noto Sans KR', 'Noto Sans CJK KR', 'Apple SD Gothic Neo', 'Malgun Gothic', sans-serif";
export const TEMPLATE_FONTS = {
    pretendard: {
        id: 'pretendard',
        name: 'Pretendard',
        description: '깔끔한 기본 고딕. 숫자와 영문이 또렷합니다.',
        family: 'SO Pretendard',
        fallback: `Pretendard, ${SANS_FALLBACK}`,
        faces: [
            { file: 'Pretendard-Medium.subset.woff2', weight: 500 },
            { file: 'Pretendard-Bold.subset.woff2', weight: 700 },
            { file: 'Pretendard-ExtraBold.subset.woff2', weight: 800 },
        ],
    },
    'nanum-square-neo': {
        id: 'nanum-square-neo',
        name: '나눔스퀘어 네오',
        description: '넓고 단단한 고딕. 멀리서도 잘 읽히는 제목용입니다.',
        family: 'SO NanumSquareNeo',
        fallback: `'SO Pretendard', ${SANS_FALLBACK}`,
        faces: [
            // 500 이 없다 — 500 을 부르면 400(Rg)이 쓰인다.
            { file: 'NanumSquareNeo-bRg.woff2', weight: 400 },
            { file: 'NanumSquareNeo-cBd.woff2', weight: 700 },
            { file: 'NanumSquareNeo-dEb.woff2', weight: 800 },
        ],
    },
    'noto-serif-kr': {
        id: 'noto-serif-kr',
        name: '본명조',
        description: '격식 있는 명조. 공지·행사 안내에 어울립니다.',
        family: 'SO NotoSerifKR',
        fallback: "'Noto Serif KR', 'Noto Serif CJK KR', 'Nanum Myeongjo', 'AppleMyungjo', 'Batang', serif",
        faces: [
            // 원본 굵기는 Medium·SemiBold·Bold — 명조의 Black(900)은 지나치게 무겁다.
            { file: 'NotoSerifKR-Medium.subset.woff2', weight: 500 },
            { file: 'NotoSerifKR-SemiBold.subset.woff2', weight: 700 },
            { file: 'NotoSerifKR-Bold.subset.woff2', weight: 800 },
        ],
    },
};
/** 이 글꼴의 `font-family` 값 — 고른 글꼴, 그다음 물러날 글꼴. */
export function templateFontStack(id) {
    const font = TEMPLATE_FONTS[id];
    return `'${font.family}', ${font.fallback}`;
}
/**
 * 세 글꼴의 `@font-face` — 받는 쪽이 파일 이름마다 자기 번들의 주소를 준다.
 *
 * 주소를 모르는 파일은 건너뛴다 — 그 굵기는 가까운 굵기나 물러날 글꼴로 그려진다(깨지지 않는다).
 * `font-display: block` — 번들에서 읽어 금방 오므로, 잠깐 기기 글꼴로 그렸다가 바뀌는 깜빡임보다
 * 낫다(옛 웹뷰는 이 속성을 모르지만 기본 동작이 같다).
 */
export function templateFontFaceCss(urlOf) {
    const rules = [];
    for (const font of Object.values(TEMPLATE_FONTS)) {
        for (const face of font.faces) {
            const url = urlOf(face.file);
            if (!url)
                continue;
            rules.push(`@font-face{font-family:'${font.family}';src:url('${url}') format('woff2');` +
                `font-weight:${face.weight};font-style:normal;font-display:block}`);
        }
    }
    return rules.join('\n');
}
/** 받는 쪽이 모은 파일 주소 목록(`{경로: 주소}`)에서 파일 이름으로 찾는 함수를 만든다. */
export function fontUrlLookup(urls) {
    const byName = new Map();
    for (const [key, url] of Object.entries(urls))
        byName.set(key.slice(key.lastIndexOf('/') + 1), url);
    return (file) => byName.get(file);
}
//# sourceMappingURL=fonts.js.map